import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import styles from "./PassportAdminReviewTable.module.css";

import PassportAdminReviewItem from "../PassportAdminReviewItem";
import DashboardTablePagination from "components/DashboardTablePagination";

import { HeaderSection, InputBox } from "@cpnw/ui";
import { Tooltip } from "react-tooltip";
import { DashboardItemsPerPage } from "components/DashboardItemsPerPage";

export default function PassportAdminReviewTable({
  items,
  onItemView = () => { },
}) {
  const [data, setData] = useState(items);

  const [filterSchool, setFilterSchool] = useState("");
  const [filterProgram, setFilterProgram] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSearch, setFilterSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const limitOptions = [10, 25, 50, 100];

  const onLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const getTypeName = (type) => {
    switch (type) {
      case "FACULTY":
        return "Faculty";
      case "CPNW-REVIEWER":
        return "CPNW Reviewer";
      case "EDUCATION":
        return "Education";
      default:
        return "Unknown";
    }
  };

  const handleFilter = (school, program, status, search) => {
    return items.filter((item) => {
      const itemName = item.name?.toLowerCase() || "";
      const itemId = item.sid?.toLowerCase() || "";
      const itemSchool = item.school?.toLowerCase() || "";
      const itemProgram = item.program?.toLowerCase() || "";
      const itemStatus = item.reviewStatus?.toLowerCase() || "";

      if (
        school &&
        !itemSchool.includes(school.trim().toLowerCase())
      ) {
        return false;
      }

      if (
        program &&
        !itemProgram.includes(program.trim().toLowerCase())
      ) {
        return false;
      }

      if (
        status &&
        !itemStatus.includes(status.trim().toLowerCase())
      ) {
        return false;
      }

      if (search) {
        const keyword = search.trim().toLowerCase();

        const found =
          itemName.includes(keyword) ||
          itemId.includes(keyword) ||
          itemSchool.includes(keyword) ||
          itemProgram.includes(keyword) ||
          itemStatus.includes(keyword);

        if (!found) return false;
      }

      return true;
    });
  };

  useEffect(() => {
    const filtered = handleFilter(
      filterSchool,
      filterProgram,
      filterStatus,
      filterSearch
    );

    setCurrentPage(1);
    setData(filtered);
  }, [
    items,
    filterSchool,
    filterProgram,
    filterStatus,
    filterSearch,
  ]);

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * limit;
    const end = start + limit;

    return data.slice(start, end);
  }, [data, currentPage, limit]);

  return (
    <div className={styles.container}>
      <HeaderSection header="Review Submissions" />

      {/* ===========================
          Filters
      ============================ */}

      <div className={styles.filters}>
        <InputBox
          placeholder="Search School"
          setValue={(e) => setFilterSchool(e.target.value)}
        />

        <InputBox
          placeholder="Search Program"
          setValue={(e) => setFilterProgram(e.target.value)}
        />

        <InputBox
          placeholder="Search Status"
          setValue={(e) => setFilterStatus(e.target.value)}
        />

        <InputBox
          placeholder="Search Anything"
          setValue={(e) => setFilterSearch(e.target.value)}
        />
      </div>

      <div className={styles.tableOptionContainer}>
        <div className={styles.itemsPerPageContent}>
          <DashboardItemsPerPage
            disabled={false}
            limit={limit}
            limitOptions={limitOptions}
            onLimitChange={onLimitChange}
          />
        </div>
      </div>

      {/* ===========================
            Table
      ============================ */}

      <div className={styles.tableWrapper}>
        {/* Header (Desktop Only) */}

        <div className={styles.tableHeader}>
          <div>Name</div>
          <div className={styles.center}>Verified</div>
          <div>Status</div>
          <div>Program</div>
          <div>School</div>
          <div>SID / EID</div>
          <div className={styles.center}>View</div>
        </div>

        {/* Rows */}

        <div className={styles.tableBody}>
          {currentItems.length > 0 ? (
            currentItems.map((item, index) => (
              <PassportAdminReviewItem
                key={`${item.sid}-${index}`}
                info={item}
                onView={() => onItemView(item)}
                even={index % 2 === 1}
              />
            ))
          ) : (
            <div className={styles.emptyState}>
              No items found.
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}

      <div className={styles.footer}>
        <DashboardTablePagination
          total={data.length}
          perPage={limit}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Tooltip */}

      <Tooltip
        id="my-tooltip"
        render={({ activeAnchor }) => (
          <span>
            Verified By{" "}
            {getTypeName(
              activeAnchor?.getAttribute("data-tooltip-type")
            )}
          </span>
        )}
      />
    </div>
  );
}

PassportAdminReviewTable.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      sid: PropTypes.string,
      school: PropTypes.string,
      program: PropTypes.string,
      reviewStatus: PropTypes.string,
      VerificationComplete: PropTypes.bool,
      VerifiedByType: PropTypes.string,
    })
  ).isRequired,

  onItemView: PropTypes.func,
};